import React, { useState } from "react";
import {
  Modal,
  Button,
  Upload,
  Alert,
  Space,
  Typography,
  Spin,
  Card,
  Radio,
} from "antd";
import Table from 'components/Common/Table';
import {
  DownloadOutlined,
  ExportOutlined,
  InboxOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { downloadInvoiceLinesTemplate, importInvoiceLines } from "api/api";

const { Dragger } = Upload;
const { Text } = Typography;

/**
 * Modal component for importing invoice lines from Excel
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is visible
 * @param {function} props.onClose - Callback when modal is closed
 * @param {function} props.onImport - Callback with parsed items when import is confirmed
 * @param {function} props.onExport - Callback to download the invoice's current lines
 * @param {Array} props.taxRates - Available tax rates for displaying names
 * @param {string} props.invoiceId - Invoice being edited; scopes line ids in the upload
 * @param {boolean} props.canExport - Whether there are saved lines to export
 */
const ImportInvoiceLinesModal = ({
  open,
  onClose,
  onImport,
  onExport,
  taxRates = [],
  invoiceId,
  canExport = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parseMessage, setParseMessage] = useState("");
  // "replace" makes the file the full list of lines, so a row deleted in Excel
  // removes that line. "append" only adds what the file contains.
  const [importMode, setImportMode] = useState("append");

  // Reset state when modal opens/closes
  const handleClose = () => {
    setParsedItems([]);
    setErrors([]);
    setUploadedFile(null);
    setParseMessage("");
    setImportMode("append");
    onClose();
  };

  // Download template
  const handleDownloadTemplate = async () => {
    setDownloadLoading(true);
    try {
      await downloadInvoiceLinesTemplate();
    } catch (err) {
      console.error("Failed to download template:", err);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    // Validate file type
    if (!file.name.endsWith(".xlsx")) {
      setErrors([{ row: 0, field: "file", message: "File must be .xlsx format" }]);
      return false;
    }

    setLoading(true);
    setUploadedFile(file);
    setParsedItems([]);
    setErrors([]);
    setParseMessage("");

    try {
      const result = await importInvoiceLines(file, invoiceId);

      if (result.items && result.items.length > 0) {
        setParsedItems(result.items);
        // A file carrying line ids is an export of this invoice, so the natural
        // reading is "these are the lines" rather than "add these".
        setImportMode(result.items.some((item) => item.id) ? "replace" : "append");
      }

      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      }

      setParseMessage(result.message || "");
    } catch (err) {
      console.error("Failed to parse file:", err);
      setErrors([{ row: 0, field: "file", message: "Failed to parse file" }]);
    } finally {
      setLoading(false);
    }

    // Prevent auto-upload
    return false;
  };

  // Confirm import
  const handleConfirmImport = () => {
    if (parsedItems.length > 0) {
      onImport(parsedItems, importMode);
      handleClose();
    }
  };

  // Get tax rate name by ID
  const getTaxRateName = (taxRateId) => {
    if (!taxRateId) return "-";
    const taxRate = taxRates.find((tr) => tr.id === taxRateId);
    return taxRate ? `${taxRate.title} ${taxRate.rate}%` : taxRateId;
  };

  // Preview table columns
  const previewColumns = [
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (value) =>
        value ? (
          <Text type="warning">Update</Text>
        ) : (
          <Text type="success">Add</Text>
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      width: "20%",
      ellipsis: true,
    },
    {
      title: "HSN",
      dataIndex: "hsn",
      width: "10%",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      width: "10%",
    },
    {
      title: "Price",
      dataIndex: "price",
      width: "12%",
      align: "right",
      render: (value) => (value ? value.toFixed(2) : "0.00"),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      width: "8%",
      align: "right",
    },
    {
      title: "Line Total",
      dataIndex: "line_total",
      width: "12%",
      align: "right",
      render: (value) => (value ? value.toFixed(2) : "0.00"),
    },
    {
      title: "Tax Rate 1",
      dataIndex: "tax_rate1",
      width: "14%",
      render: (value) => getTaxRateName(value),
    },
    {
      title: "Tax Rate 2",
      dataIndex: "tax_rate2",
      width: "14%",
      render: (value) => getTaxRateName(value),
    },
  ];

  // Error table columns
  const errorColumns = [
    {
      title: "Row",
      dataIndex: "row",
      width: "15%",
    },
    {
      title: "Field",
      dataIndex: "field",
      width: "20%",
    },
    {
      title: "Error",
      dataIndex: "message",
      width: "65%",
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: "#217346" }} />
          <span>Import Invoice Lines from Excel</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="import"
          type="primary"
          onClick={handleConfirmImport}
          disabled={parsedItems.length === 0}
          icon={<CheckCircleOutlined />}
        >
          Import {parsedItems.length > 0 ? `${parsedItems.length} Item(s)` : ""}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Step 1: Start from an export or a blank template */}
        <Card size="small" title="Step 1: Get an Excel File to Edit">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">
              Export this invoice's current lines to edit them in Excel, or start
              from a blank template to add new lines. An exported file keeps each
              line's id, so importing it back updates those lines instead of
              adding copies — clear the id to turn a row into a new line, and
              delete a row to remove that line.
            </Text>
            <Space wrap>
              <Button
                type="primary"
                ghost
                icon={<ExportOutlined />}
                onClick={onExport}
                disabled={!canExport}
                title={
                  canExport
                    ? "Download this invoice's lines"
                    : "Save the invoice with at least one line to export"
                }
              >
                Export Current Lines
              </Button>
              <Button
                type="default"
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
                loading={downloadLoading}
              >
                Download Blank Template
              </Button>
            </Space>
          </Space>
        </Card>

        {/* Step 2: Upload File */}
        <Card size="small" title="Step 2: Upload Filled Excel">
          <Dragger
            name="file"
            multiple={false}
            accept=".xlsx"
            beforeUpload={handleFileUpload}
            showUploadList={false}
            disabled={loading}
          >
            {loading ? (
              <div style={{ padding: "20px" }}>
                <Spin size="large" />
                <p style={{ marginTop: 16 }}>Processing file...</p>
              </div>
            ) : (
              <>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Only .xlsx files are supported
                </p>
              </>
            )}
          </Dragger>

          {uploadedFile && !loading && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Uploaded: <Text strong>{uploadedFile.name}</Text>
              </Text>
            </div>
          )}
        </Card>

        {/* Errors Display */}
        {errors.length > 0 && (
          <Alert
            message={
              <Space>
                <ExclamationCircleOutlined />
                <span>
                  {errors.length} error(s) found in the uploaded file
                </span>
              </Space>
            }
            description={
              <Table
                dataSource={errors.map((err, idx) => ({ ...err, key: idx }))}
                columns={errorColumns}
                size="small"
                pagination={false}
                style={{ marginTop: 8 }}
                scroll={{ y: 150 }}
              />
            }
            type="error"
            showIcon={false}
          />
        )}

        {/* Success Message */}
        {parseMessage && parsedItems.length > 0 && errors.length === 0 && (
          <Alert
            message={parseMessage}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        )}

        {/* Partial Success Message */}
        {parseMessage && parsedItems.length > 0 && errors.length > 0 && (
          <Alert
            message={parseMessage}
            description="Some rows had errors and were skipped. You can still import the valid items."
            type="warning"
            showIcon
          />
        )}

        {/* Step 3: How the file should be applied */}
        {parsedItems.length > 0 && (
          <Card size="small" title="Step 3: Apply To The Invoice">
            <Radio.Group
              value={importMode}
              onChange={(e) => setImportMode(e.target.value)}
            >
              <Space direction="vertical">
                <Radio value="replace">
                  <Text strong>Replace all lines with this file</Text>
                  <br />
                  <Text type="secondary">
                    The invoice ends up with exactly these {parsedItems.length}{" "}
                    line(s). Lines missing from the file are removed.
                  </Text>
                </Radio>
                <Radio value="append">
                  <Text strong>Add these lines to the invoice</Text>
                  <br />
                  <Text type="secondary">
                    Existing lines are kept. Rows carrying an id update that line;
                    the rest are added.
                  </Text>
                </Radio>
              </Space>
            </Radio.Group>
          </Card>
        )}

        {/* Preview Table */}
        {parsedItems.length > 0 && (
          <Card size="small" title={`Preview (${parsedItems.length} items)`}>
            <Table
              dataSource={parsedItems.map((item, idx) => ({ ...item, key: idx }))}
              columns={previewColumns}
              size="small"
              pagination={parsedItems.length > 10 ? { defaultPageSize: 10, showSizeChanger: true } : false}
              scroll={{ x: 800 }}
            />
          </Card>
        )}
      </Space>
    </Modal>
  );
};

export default ImportInvoiceLinesModal;
